import { ComponentType, lazy, ReactElement, Suspense } from 'react'

import { Navigate, Outlet, useRoutes } from 'react-router-dom'

import { SHARED_WITH_ME } from 'const/dashboards'
import { type DynamicModule } from 'dynamicModules/types'
import { userHasDashboardsOnlyPermissions } from 'helpers/featurePermissions'
import { useUserHasPermissions } from 'hooks'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useGatedFeatures } from 'hooks/useGatedFeatures'
import { RemoteModule } from 'remoteModules/RemoteModule'
import { RedirectToAdvertisingView } from 'views/AmazonAdvertisingSummaryPage/components/RedirectToAdvertisingView'

import DynamicPageRoute, { DynamicPageProps } from './DynamicPageRoute'
import { getPath } from './pagePaths'
import { PageRoute } from './PageRoute'
import { RouteLoadingPlaceholder } from './RouteLoadingPlaceholder'
import { dashboardsUrl } from './urls'
import { useRocketComponentProps } from './useRocketComponentProps'

const loading = <RouteLoadingPlaceholder />

const isDevEnvironment = process.env.NODE_ENV === 'development'

export const initDynamicModule = <T extends DynamicPageProps>(
    module: DynamicModule
): { default: ComponentType<T> } => {
    return { default: DynamicPageRoute(module) }
}

const DashboardPage = lazy(
    () =>
        import(
            /* webpackChunkName: "dashboards" */ 'views/DashboardPage/DashboardPage'
        )
)
const DashboardsSummaryPage = lazy(
    () =>
        import(
            /* webpackChunkName: "dashboards" */ 'views/DashboardsSummaryPage/DashboardsSummaryPage'
        )
)
const HomePage = lazy(
    () => import(/* webpackChunkName: "dashboards" */ 'views/HomePage/HomePage')
)
const AlertCreationPage = lazy(() =>
    import(/* webpackChunkName: "settings" */ 'dynamicModules/Settings/').then(
        (module) => initDynamicModule(module.AlertCreationPage)
    )
)
const AlertPage = lazy(() =>
    import(/* webpackChunkName: "settings" */ 'dynamicModules/Settings/').then(
        (module) => initDynamicModule(module.AlertPage)
    )
)
const AmazonAdGroupPage = lazy(
    () =>
        import(
            /* webpackChunkName: "amazon" */ 'views/AmazonAdGroupPage/AmazonAdGroupPage'
        )
)
const AmazonCampaignCreationPage = lazy(
    () =>
        import(
            /* webpackChunkName: "amazon" */ 'views/AmazonCampaignCreationPage/AmazonCampaignCreationPage'
        )
)
const AmazonCampaignPage = lazy(
    () =>
        import(
            /* webpackChunkName: "amazon" */ 'views/AmazonCampaignPage/AmazonCampaignPage'
        )
)
const AmazonAdvertisingSummaryPage = lazy(
    () =>
        import(
            /* webpackChunkName: "amazon" */ 'views/AmazonAdvertisingSummaryPage/AmazonAdvertisingSummaryPage'
        )
)
const AmazonKeywordPage = lazy(
    () =>
        import(
            /* webpackChunkName: "amazon" */ 'views/AmazonKeywordPage/AmazonKeywordPage'
        )
)
const AmazonPortfolioPage = lazy(
    () =>
        import(
            /* webpackChunkName: "amazon" */ 'views/AmazonPortfolioPage/AmazonPortfolioPage'
        )
)
const AmazonProductAdPage = lazy(
    () =>
        import(
            /* webpackChunkName: "amazon" */ 'views/AmazonProductAdPage/AmazonProductAdPage'
        )
)
const AmazonProductPage = lazy(
    () =>
        import(
            /* webpackChunkName: "amazon" */ 'views/AmazonProductPage/AmazonProductPage'
        )
)
const AutomationPage = lazy(
    () =>
        import(
            /* webpackChunkName: "automation" */ 'views/AutomationPage/AutomationPage'
        )
)
const AutomationProfilePage = lazy(
    () =>
        import(
            /* webpackChunkName: "automation" */ 'views/AutomationProfilePage/AutomationProfilePage'
        )
)
const RulebookPage = lazy(
    () =>
        import(
            /* webpackChunkName: "automation" */ 'views/RulebookPage/RulebookPage'
        )
)
const BrandPage = lazy(
    () => import(/* webpackChunkName: "brands" */ 'views/BrandPage/BrandPage')
)
const ColumnsDebuggerPage = lazy(() =>
    import(/* webpackChunkName: "devTools" */ 'dynamicModules/DevTools').then(
        (module) => initDynamicModule(module.ColumnsDebugger)
    )
)
const CategoryPerformanceSummaryPage = lazy(() =>
    import(
        /* webpackChunkName: "categoryPerformance" */ 'dynamicModules/CategoryPerformance'
    ).then((module) => initDynamicModule(module.CategoryPerformanceSummaryPage))
)
const CategoryDetailsPage = lazy(() =>
    import(
        /* webpackChunkName: "categoryPerformance" */ 'dynamicModules/CategoryPerformance'
    ).then((module) => initDynamicModule(module.CategoryDetailsPage))
)
const DspAdvertiserPage = lazy(
    () =>
        import(
            /* webpackChunkName: "dsp" */ 'views/DspAdvertiserPage/DspAdvertiserPage'
        )
)
const DspLineItemPage = lazy(
    () =>
        import(
            /* webpackChunkName: "dsp" */ 'views/DspLineItemPage/DspLineItemPage'
        )
)
const DspOrderPage = lazy(
    () =>
        import(/* webpackChunkName: "dsp" */ 'views/DspOrderPage/DspOrderPage')
)
const DspSummaryPage = lazy(
    () =>
        import(
            /* webpackChunkName: "dsp" */ 'views/DspSummaryPage/DspSummaryPage'
        )
)
const IconsPage = lazy(() =>
    import(/* webpackChunkName: "devTools" */ 'dynamicModules/DevTools').then(
        (module) => initDynamicModule(module.AllIcons)
    )
)
const InsightsPage = lazy(() =>
    import(
        /* webpackChunkName: "insightsNew" */ 'dynamicModules/Insights'
    ).then((module) => initDynamicModule(module.InsightsList))
)
const KeywordResearchPage = lazy(() =>
    import(
        /* webpackChunkName: "keywordResearch" */ 'dynamicModules/KeywordResearch'
    ).then((module) => initDynamicModule(module.KeywordResearch))
)
const KeywordResearchListsSummaryPage = lazy(() =>
    import(
        /* webpackChunkName: "keywordResearch" */ 'dynamicModules/KeywordResearch'
    ).then((module) => initDynamicModule(module.KeywordListsSummaryPage))
)
const KeywordResearchListDetailPage = lazy(() =>
    import(
        /* webpackChunkName: "keywordResearch" */ 'dynamicModules/KeywordResearch'
    ).then((module) => initDynamicModule(module.KeywordListDetailPage))
)
const LabelPage = lazy(() =>
    import(/* webpackChunkName: "settings" */ 'dynamicModules/Settings').then(
        (module) => initDynamicModule(module.LabelPage)
    )
)
const DistributionAnalysisPage = lazy(() =>
    import(
        /* webpackChunkName: "distributionAnalysis" */ 'dynamicModules/DistributionAnalysis'
    ).then((module) => initDynamicModule(module.DistributionAnalysisPage))
)
const MetricsDebuggerPage = lazy(() =>
    import(/* webpackChunkName: "devTools" */ 'dynamicModules/DevTools').then(
        (module) => initDynamicModule(module.MetricsDebugger)
    )
)
const MetricsGlossaryPage = lazy(() =>
    import(
        /* webpackChunkName: "metricsGlossary" */ 'dynamicModules/MetricsGlossary'
    ).then((module) => initDynamicModule(module.MetricsGlossaryList))
)
const OrganizationGroupPage = lazy(() =>
    import(/* webpackChunkName: "settings" */ 'dynamicModules/Settings').then(
        (module) => initDynamicModule(module.OrganizationGroupPage)
    )
)
const ProductCatalogPage = lazy(() =>
    import(
        /* webpackChunkName: "productCatalog" */ 'dynamicModules/ProductCatalog'
    ).then((module) => initDynamicModule(module.ProductCatalog))
)
const ProductDetailPage = lazy(() =>
    import(
        /* webpackChunkName: "productCatalog" */ 'dynamicModules/ProductCatalog'
    ).then((module) => initDynamicModule(module.ProductDetail))
)
const ReportsSummaryPage = lazy(
    () =>
        import(
            /* webpackChunkName: "reports" */ 'views/ReportsSummaryPage/ReportsSummaryPage'
        )
)
const SandboxPage = lazy(() =>
    import(/* webpackChunkName: "devTools" */ 'dynamicModules/DevTools').then(
        (module) => initDynamicModule(module.Sandbox)
    )
)
const SegmentCreatePage = lazy(() =>
    import(
        /* webpackChunkName: "segments" */ 'dynamicModules/SegmentManagement'
    ).then((module) => initDynamicModule(module.SegmentCreate))
)
const SegmentEditPage = lazy(() =>
    import(
        /* webpackChunkName: "segments" */ 'dynamicModules/SegmentManagement'
    ).then((module) => initDynamicModule(module.SegmentEdit))
)
const SegmentDetailPage = lazy(() =>
    import(/* webpackChunkName: "segments" */ 'dynamicModules/Segments').then(
        (module) => initDynamicModule(module.SegmentDetail)
    )
)
const SegmentsSummaryPage = lazy(() =>
    import(/* webpackChunkName: "segments" */ 'dynamicModules/Segments').then(
        (module) => initDynamicModule(module.SegmentsList)
    )
)
const SettingsPage = lazy(() =>
    import(/* webpackChunkName: "settings" */ 'dynamicModules/Settings').then(
        (module) => initDynamicModule(module.SettingsPage)
    )
)
const SovKeywordPage = lazy(() =>
    import(/* webpackChunkName: "sov" */ 'dynamicModules/SovKeywords').then(
        (module) => initDynamicModule(module.SovKeywordDetail)
    )
)
const SovKeywordsSummaryPage = lazy(() =>
    import(/* webpackChunkName: "sov" */ 'dynamicModules/SovKeywords').then(
        (module) => initDynamicModule(module.SovOverview)
    )
)
const SovSearchResultsPage = lazy(() =>
    import(/* webpackChunkName: "sov" */ 'dynamicModules/SovKeywords').then(
        (module) => initDynamicModule(module.SovSearchResults)
    )
)
function SuspenseWrapper(props: any): ReactElement {
    return (
        <Suspense fallback={loading}>
            <Outlet {...props} />
        </Suspense>
    )
}

function PageRoutes(): ReactElement | null {
    const dashboardOnlyExperience = useUserHasPermissions(
        userHasDashboardsOnlyPermissions
    )
    const { keywordResearchV2 } = useFeatureFlags()
    const { sellerCompliance } = useGatedFeatures()
    const rocketComponentProps = useRocketComponentProps()

    return useRoutes([
        {
            element: <SuspenseWrapper />,
            children: [
                dashboardOnlyExperience
                    ? {
                          path: getPath('homePage'),
                          element: <PageRoute pageName={'homePage'} />,
                          children: [
                              {
                                  path: '',
                                  element: (
                                      <Navigate
                                          to={dashboardsUrl(SHARED_WITH_ME)}
                                      />
                                  ),
                              },
                          ],
                      }
                    : {
                          path: getPath('homePage'),
                          element: (
                              <PageRoute
                                  pageName={'homePage'}
                                  restricted={dashboardOnlyExperience}
                              />
                          ),
                          children: [
                              {
                                  path: '',
                                  element: <HomePage />,
                              },
                          ],
                      },
                {
                    path: getPath('metricsGlossaryPage'),
                    element: <PageRoute pageName={'metricsGlossaryPage'} />,
                    children: [
                        {
                            path: '',
                            element: (
                                <MetricsGlossaryPage
                                    pageName={'metricsGlossaryPage'}
                                />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('adAccountsSummaryPage'),
                    element: (
                        <PageRoute
                            pageName={'adAccountsSummaryPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <RedirectToAdvertisingView view="adAccounts" />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('adAccountPage'),
                    element: (
                        <PageRoute
                            pageName={'adAccountPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <BrandPage />,
                        },
                    ],
                },
                {
                    path: getPath('brandCampaignPage'),
                    element: (
                        <PageRoute
                            pageName={'brandCampaignPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <AmazonCampaignPage />,
                        },
                    ],
                },
                {
                    path: getPath('brandCampaignKeywordPage'),
                    element: (
                        <PageRoute
                            pageName={'brandCampaignKeywordPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <AmazonKeywordPage />,
                        },
                    ],
                },
                {
                    path: getPath('brandCampaignProductAdPage'),
                    element: (
                        <PageRoute
                            pageName={'brandCampaignProductAdPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <AmazonProductAdPage />,
                        },
                    ],
                },
                {
                    path: getPath('campaignsSummaryPage'),
                    element: (
                        <PageRoute
                            pageName={'campaignsSummaryPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <AmazonAdvertisingSummaryPage />,
                        },
                    ],
                },
                {
                    path: getPath('campaignPage'),
                    element: (
                        <PageRoute
                            pageName={'campaignPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <AmazonCampaignPage />,
                        },
                    ],
                },
                {
                    path: getPath('categoryPerformanceSummaryPage'),
                    element: (
                        <PageRoute
                            pageName={'categoryPerformanceSummaryPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <CategoryPerformanceSummaryPage
                                    pageName={'categoryPerformanceSummaryPage'}
                                />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('categoryDetailsPage'),
                    element: (
                        <PageRoute
                            pageName={'categoryDetailsPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <CategoryDetailsPage
                                    pageName={'categoryDetailsPage'}
                                />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('amazonCampaignCreationPage'),
                    element: (
                        <PageRoute
                            pageName={'amazonCampaignCreationPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <AmazonCampaignCreationPage />,
                        },
                    ],
                },
                {
                    path: getPath('campaignKeywordPage'),
                    element: (
                        <PageRoute
                            pageName={'campaignKeywordPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <AmazonKeywordPage />,
                        },
                    ],
                },
                {
                    path: getPath('campaignProductAdPage'),
                    element: (
                        <PageRoute
                            pageName={'campaignProductAdPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <AmazonProductAdPage />,
                        },
                    ],
                },
                {
                    path: getPath('adGroupPage'),
                    element: (
                        <PageRoute
                            pageName={'adGroupPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <AmazonAdGroupPage />,
                        },
                    ],
                },
                {
                    path: getPath('adGroupKeywordPage'),
                    element: (
                        <PageRoute
                            pageName={'adGroupKeywordPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <AmazonKeywordPage />,
                        },
                    ],
                },
                {
                    path: getPath('productsSummaryPage'),
                    element: (
                        <PageRoute
                            pageName={'productsSummaryPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <RedirectToAdvertisingView view="products" />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('productPage'),
                    element: (
                        <PageRoute
                            pageName={'productPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <AmazonProductPage />,
                        },
                    ],
                },
                {
                    path: getPath('productProductAdPage'),
                    element: (
                        <PageRoute
                            pageName={'productProductAdPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <AmazonProductAdPage />,
                        },
                    ],
                },
                {
                    path: getPath('productCatalogPage'),
                    element: (
                        <PageRoute
                            pageName={'productCatalogPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <ProductCatalogPage
                                    pageName={'productCatalogPage'}
                                />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('productDetailPage'),
                    element: (
                        <PageRoute
                            pageName={'productDetailPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <ProductDetailPage
                                    pageName={'productDetailPage'}
                                />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('targetingSummaryPage'),
                    element: (
                        <PageRoute
                            pageName={'targetingSummaryPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <RedirectToAdvertisingView view="targeting" />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('adGroupsSummaryPage'),
                    element: (
                        <PageRoute
                            pageName={'adGroupsSummaryPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <RedirectToAdvertisingView view="adGroups" />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('keywordPage'),
                    element: (
                        <PageRoute
                            pageName={'keywordPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [{ path: '', element: <AmazonKeywordPage /> }],
                },
                {
                    path: getPath('keywordResearchPage'),
                    element: (
                        <PageRoute
                            pageName={'keywordResearchPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <KeywordResearchPage
                                    pageName={'keywordResearchPage'}
                                />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('keywordResearchListsPage'),
                    element: (
                        <PageRoute
                            pageName={'keywordResearchListsPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <KeywordResearchListsSummaryPage
                                    pageName={'keywordResearchListsPage'}
                                />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('keywordResearchListDetailPage'),
                    element: (
                        <PageRoute
                            pageName={'keywordResearchListDetailPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <KeywordResearchListDetailPage
                                    pageName={'keywordResearchListDetailPage'}
                                />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('segmentPage'),
                    element: (
                        <PageRoute
                            pageName={'segmentPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <SegmentDetailPage pageName={'segmentPage'} />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('segmentEditPage'),
                    element: (
                        <PageRoute
                            pageName={'segmentEditPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <SegmentEditPage pageName={'segmentEditPage'} />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('segmentCreationPage'),
                    element: (
                        <PageRoute
                            pageName={'segmentCreationPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <SegmentCreatePage
                                    pageName={'segmentCreationPage'}
                                />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('segmentsSummaryPage'),
                    element: (
                        <PageRoute
                            pageName={'segmentsSummaryPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <SegmentsSummaryPage
                                    pageName={'segmentsSummaryPage'}
                                />
                            ),
                        },
                    ],
                },
                {
                    path: `${getPath('marketAnalysis')}/*`,
                    element: (
                        <PageRoute
                            pageName={'marketAnalysis'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '*',
                            element: (
                                <RemoteModule
                                    scope="rocket"
                                    module="./MarketAnalysis"
                                    componentProps={rocketComponentProps}
                                />
                            ),
                        },
                    ],
                },
                {
                    path: `${getPath('aiPage')}/*`,
                    element: (
                        <PageRoute
                            pageName={'aiPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '*',
                            element: (
                                <RemoteModule
                                    scope="rocket"
                                    module="./Ai"
                                    componentProps={rocketComponentProps}
                                />
                            ),
                        },
                    ],
                },
                {
                    path: `${getPath('keywordResearchV2Page')}/*`,
                    element: (
                        <PageRoute
                            pageName={'keywordResearchV2Page'}
                            restricted={
                                dashboardOnlyExperience || !keywordResearchV2
                            }
                        />
                    ),
                    children: [
                        {
                            path: '*',
                            element: (
                                <RemoteModule
                                    scope="rocket"
                                    module="./KeywordResearch"
                                    componentProps={rocketComponentProps}
                                />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('sovKeywordsSummaryPage'),
                    element: (
                        <PageRoute
                            pageName={'sovKeywordsSummaryPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <SovKeywordsSummaryPage
                                    pageName={'sovKeywordsSummaryPage'}
                                />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('sovKeywordPage'),
                    element: (
                        <PageRoute
                            pageName={'sovKeywordPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <SovKeywordPage pageName={'sovKeywordPage'} />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('sovKeywordSearchResultPage'),
                    element: (
                        <PageRoute
                            pageName={'sovKeywordSearchResultPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <SovSearchResultsPage
                                    pageName={'sovKeywordSearchResultPage'}
                                />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('settingsPage'),
                    element: (
                        <PageRoute
                            pageName={'settingsPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <SettingsPage pageName={'settingsPage'} />,
                        },
                        {
                            path: '*',
                            element: <SettingsPage pageName={'settingsPage'} />,
                        },
                    ],
                },
                {
                    path: getPath('organizationGroupPage'),
                    element: (
                        <PageRoute
                            pageName={'organizationGroupPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <OrganizationGroupPage
                                    pageName={'organizationGroupPage'}
                                />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('distributionAnalysisPage'),
                    element: (
                        <PageRoute
                            pageName={'distributionAnalysisPage'}
                            restricted={
                                dashboardOnlyExperience || !sellerCompliance
                            }
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <DistributionAnalysisPage
                                    pageName={'distributionAnalysisPage'}
                                />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('automationPage'),
                    element: (
                        <PageRoute
                            pageName={'automationPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <AutomationPage />,
                        },
                    ],
                },
                {
                    path: getPath('automationProfilePage'),
                    element: (
                        <PageRoute
                            pageName={'automationProfilePage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <AutomationProfilePage />,
                        },
                    ],
                },
                {
                    path: getPath('labelPage'),
                    element: (
                        <PageRoute
                            pageName={'labelPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <LabelPage pageName={'labelPage'} />,
                        },
                    ],
                },
                {
                    path: getPath('dashboardsSummaryPage'),
                    element: <PageRoute pageName={'dashboardsSummaryPage'} />,
                    children: [
                        {
                            path: '',
                            element: <DashboardsSummaryPage />,
                        },
                    ],
                },
                {
                    path: getPath('dashboardPage'),
                    element: <PageRoute pageName={'dashboardPage'} />,
                    children: [
                        {
                            path: '',
                            element: <DashboardPage />,
                        },
                    ],
                },
                {
                    path: getPath('reportsSummaryPage'),
                    element: <PageRoute pageName={'reportsSummaryPage'} />,
                    children: [
                        {
                            path: '',
                            element: <ReportsSummaryPage />,
                        },
                    ],
                },
                {
                    path: getPath('portfoliosSummaryPage'),
                    element: (
                        <PageRoute
                            pageName={'portfoliosSummaryPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <RedirectToAdvertisingView view="portfolios" />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('portfolioPage'),
                    element: (
                        <PageRoute
                            pageName={'portfolioPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <AmazonPortfolioPage />,
                        },
                    ],
                },
                {
                    path: getPath('alertPage'),
                    element: (
                        <PageRoute
                            pageName={'alertPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <AlertPage pageName={'alertPage'} />,
                        },
                    ],
                },
                {
                    path: getPath('alertCreationPage'),
                    element: (
                        <PageRoute
                            pageName={'alertCreationPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <AlertCreationPage
                                    pageName={'alertCreationPage'}
                                />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('debugColumnsPage'),
                    element: (
                        <PageRoute
                            pageName={'debugColumnsPage'}
                            restricted={!isDevEnvironment}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <ColumnsDebuggerPage
                                    pageName={'debugColumnsPage'}
                                />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('debugIconsPage'),
                    element: (
                        <PageRoute
                            pageName={'debugIconsPage'}
                            restricted={!isDevEnvironment}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <IconsPage pageName={'debugIconsPage'} />,
                        },
                    ],
                },
                {
                    path: getPath('debugMetricsPage'),
                    element: (
                        <PageRoute
                            pageName={'debugMetricsPage'}
                            restricted={!isDevEnvironment}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <MetricsDebuggerPage
                                    pageName={'debugMetricsPage'}
                                />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('debugSandboxPage'),
                    element: (
                        <PageRoute
                            pageName={'debugSandboxPage'}
                            restricted={!isDevEnvironment}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: (
                                <SandboxPage pageName={'debugSandboxPage'} />
                            ),
                        },
                    ],
                },
                {
                    path: getPath('insightsPage'),
                    element: (
                        <PageRoute
                            pageName={'insightsPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <InsightsPage pageName={'insightsPage'} />,
                        },
                    ],
                },
                {
                    path: getPath('rulebookPage'),
                    element: (
                        <PageRoute
                            pageName={'rulebookPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <RulebookPage />,
                        },
                    ],
                },
                {
                    path: getPath('dspSummaryPage'),
                    element: (
                        <PageRoute
                            pageName={'dspSummaryPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <DspSummaryPage />,
                        },
                    ],
                },
                {
                    path: getPath('dspAdvertiserPage'),
                    element: (
                        <PageRoute
                            pageName={'dspAdvertiserPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <DspAdvertiserPage />,
                        },
                    ],
                },
                {
                    path: getPath('dspOrderPage'),
                    element: (
                        <PageRoute
                            pageName={'dspOrderPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <DspOrderPage />,
                        },
                    ],
                },
                {
                    path: getPath('dspLineItemPage'),
                    element: (
                        <PageRoute
                            pageName={'dspLineItemPage'}
                            restricted={dashboardOnlyExperience}
                        />
                    ),
                    children: [
                        {
                            path: '',
                            element: <DspLineItemPage />,
                        },
                    ],
                },
            ],
        },
    ])
}

export default PageRoutes
